/**
 * Minimal EditorJS OutputData-like shape for conversion (avoids runtime dependency on @editorjs/editorjs).
 */
interface EditorBlock {
	type: string
	data?: Record<string, unknown>
}

interface EditorOutputLike {
	blocks?: EditorBlock[]
}

/** Nested list item: content string and optional nested items */
interface NestedListItem {
	content?: string
	items?: NestedListItem[]
}

/**
 * Renders nested list items to HTML (content may contain inline HTML e.g. <strong>).
 */
function renderNestedListItems(
	items: NestedListItem[],
	style: string,
	escapeContent: (text: string) => string
): string {
	if (!items?.length) return ''
	const tag = style === 'ordered' ? 'ol' : 'ul'
	const lis = items
		.map(item => {
			const content = (item.content ?? '').trim()
			const escaped = escapeContent(content)
			const nested = renderNestedListItems(item.items ?? [], style, escapeContent)
			const inner = nested ? `${escaped}${nested}` : escaped
			return `<li>${inner}</li>`
		})
		.join('')
	return `<${tag}>${lis}</${tag}>`
}

/**
 * Converts EditorJS OutputData blocks to HTML string.
 * Handles paragraph, list, nestedList and header block types.
 */
export function editorJsBlocksToHtml(output: EditorOutputLike | null | undefined): string {
	if (!output?.blocks?.length) return ''

	return output.blocks
		.map(block => {
			const data = block.data ?? {}
			switch (block.type) {
				case 'paragraph': {
					const text = (data.text as string) ?? ''
					return text ? `<p>${escapeHtml(text)}</p>` : ''
				}
				case 'list': {
					const items = (data.items as string[]) ?? []
					const style = (data.style as string) ?? 'unordered'
					const tag = style === 'ordered' ? 'ol' : 'ul'
					const lis = items.map(item => `<li>${escapeHtml(item)}</li>`).join('')
					return items.length ? `<${tag}>${lis}</${tag}>` : ''
				}
				case 'nestedList': {
					const items = (data.items as NestedListItem[]) ?? []
					const style = (data.style as string) ?? 'unordered'
					if (!items.length) return ''
					return renderNestedListItems(items, style, escapeHtml)
				}
				case 'header': {
					const text = (data.text as string) ?? ''
					const level = Math.min(6, Math.max(1, (data.level as number) ?? 1))
					return text ? `<h${level}>${escapeHtml(text)}</h${level}>` : ''
				}
				default:
					return ''
			}
		})
		.filter(Boolean)
		.join('')
}


function escapeHtml(text: string): string {
	const map: Record<string, string> = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		"'": '&#39;'
	}
	return text.replace(/[&<>"']/g, ch => map[ch] ?? ch)
}
